import React, { useEffect, useState } from "react"
import { MdFavoriteBorder, MdAdd } from "react-icons/md"
import FastMovingBadgePreview from "./FastMovingBadgePreview"
import PriceSymbol from "./PriceSymbol"
import styles from "./MobileItemCardPreview.module.css"

const FALLBACK_IMAGE =
  "data:image/svg+xml," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="220" height="180" viewBox="0 0 220 180"><rect fill="#f7f7f7" width="220" height="180"/><text x="50%" y="50%" fill="#b0b0b0" font-family="sans-serif" font-size="13" text-anchor="middle" dy=".3em">No image</text></svg>`
  )

const stop = e => {
  e.preventDefault()
  e.stopPropagation()
}

const resolveImageSrc = (itemImage, fallbackImage) =>
  itemImage || fallbackImage || FALLBACK_IMAGE

/**
 * Shared mobile item-card preview.
 * Offer % uses the same originalPrice > price rule as offer-items API.
 * Fast-moving uses delivery-settings iconUrl (plain truck icon, mobile style).
 */
const MobileItemCardPreview = ({
  item,
  fallbackImage = "",
  fastMovingIconUrl,
  fastMovingSettingsEnabled,
}) => {
  const badges = Array.isArray(item?.badges) ? item.badges : []
  const hasOffer = item?.discountPercent != null && item.discountPercent > 0
  const [imageSrc, setImageSrc] = useState(() =>
    resolveImageSrc(item?.image, fallbackImage)
  )

  useEffect(() => {
    setImageSrc(resolveImageSrc(item?.image, fallbackImage))
  }, [item?.image, fallbackImage])

  const handleImageError = () => {
    if (fallbackImage && imageSrc !== fallbackImage) {
      setImageSrc(fallbackImage)
      return
    }
    if (imageSrc !== FALLBACK_IMAGE) {
      setImageSrc(FALLBACK_IMAGE)
    }
  }

  return (
    <article className={styles.card}>
      <div className={styles.media}>
        {hasOffer && (
          <span className={styles.discount}>
            <span dir="ltr">-{item.discountPercent}%</span>
          </span>
        )}
        <FastMovingBadgePreview
          isFastMoving={item?.isFastMoving}
          iconUrl={fastMovingIconUrl}
          settingsEnabled={fastMovingSettingsEnabled}
          className={`${styles.fastMovingBadge}${
            hasOffer ? ` ${styles.fastMovingBelowOffer}` : ""
          }`}
          size={42}
          placement="overlay"
          variant="plain"
        />
        <span
          className={styles.favourite}
          aria-hidden="true"
          onClick={stop}
          onMouseDown={stop}
        >
          <MdFavoriteBorder size={22} />
        </span>
        <div className={styles.imageWrap}>
          <img
            src={imageSrc}
            alt={item?.name || "Item"}
            onError={handleImageError}
          />
          <span
            className={styles.addBtn}
            aria-hidden="true"
            onClick={stop}
            onMouseDown={stop}
          >
            <MdAdd size={22} />
          </span>
        </div>
      </div>

      <div className={styles.body}>
        <span className={styles.name}>{item?.name || "Item name"}</span>
        <div className={styles.pricing}>
          {item?.price != null && (
            <span className={styles.price}>
              <PriceSymbol className={styles.priceSymbol} size={14} />
              <span>{Number(item.price).toFixed(2)}</span>
            </span>
          )}
          {item?.compareAtPrice != null && (
            <span className={styles.compare}>
              <PriceSymbol className={styles.priceSymbol} size={12} />
              <span>{Number(item.compareAtPrice).toFixed(2)}</span>
            </span>
          )}
        </div>
        {badges.length > 0 && (
          <div className={styles.badges}>
            {badges.map(badge => (
              <span key={badge} className={styles.badge}>
                {badge}
              </span>
            ))}
          </div>
        )}
      </div>
    </article>
  )
}

export default MobileItemCardPreview
